// BLOQUE 27 PARTE A — Filtro reutilizable Cliente -> Obra -> Presupuesto (cascada),
// extraído de la implementación original de Pedidos de Materiales (Bloque 16 Parte B)
// para poder montarlo en el resto de las pestañas de Compras (Aprobaciones, Ped.
// Proveedores, Análisis Ppto., Ptos. Finales, Órdenes de Compra, Facturas, Retiro/
// Entrega). El estado del filtro vive en el componente padre (Purchases.jsx) y se
// comparte entre pestañas — si el usuario elige una obra en una pestaña, la ve
// filtrada en el resto.
import SelectBuscable from "../../components/SelectBuscable";

export const OBRA_RAPIDA_FILTER = "__obra_rapida__";

// R10: el ID del maestro prevalece. Un nombre histórico sólo coincide exactamente
// con un único cliente; nunca se deduce identidad quitando sociedades o por includes.
const _nombreCliente = s => String(s || "").trim().replace(/\s+/g, " ").toLowerCase();
const _mismoCliente = (a, b, clientes) => {
  const x = _nombreCliente(a), y = _nombreCliente(b);
  return !!x && x === y && clientes.filter(c => _nombreCliente(c.cliente) === y).length === 1;
};

// Fix DY (2026-09-23): "una vez que se selecciona cliente (…) en presupuesto
// deberían salir todos los presupuestos de ese cliente nomás" (Edson, 23/09).
// Antes el cliente solo filtraba las obras: sin obra elegida salían los
// presupuestos aprobados de TODOS los clientes. Un presupuesto es del cliente por
// su `cliente_id`; si no lo tiene, por la obra (`obra_id`) o por el nombre del
// cliente. Con obra elegida, el criterio de obra es el mismo de siempre. Lo usan
// este filtro, el formulario del Pedido y "Vincular a presupuesto".
export function filtrarPresupuestos(allBudgets, { clienteId = "", obraSel = null, listasClientes = [], listasObras = [] } = {}) {
  const cliente = clienteId ? listasClientes.find(c => c._id === clienteId) : null;
  const clienteDeObra = new Map(listasObras.map(o => [o._id, o.cliente_id]));
  return allBudgets.filter(b => {
    if (b.status !== "aprobado") return false;
    // Primero el ID propio o el ID de la obra; sin vínculo, sólo nombre exacto e inequívoco.
    if (clienteId) {
      if (b.cliente_id) {
        if (b.cliente_id !== clienteId) return false;
      } else {
        const clienteObra = b.obra_id ? clienteDeObra.get(b.obra_id) : null;
        if (clienteObra) {
          if (clienteObra !== clienteId) return false;
        } else if (!_mismoCliente(b.cliente, cliente?.cliente, listasClientes)) return false;
      }
    }
    if (!obraSel) return true;
    return (b.obra_id && b.obra_id === obraSel._id) ||
      (!b.obra_id && (
        (b.obra || "").toLowerCase().includes((obraSel.obra || "").toLowerCase()) ||
        (b.obra || "").toLowerCase().includes((obraSel.codigo || "").toLowerCase())
      ));
  });
}

export function FiltroClienteObraPresupuesto({
  listasClientes = [],
  listasObras = [],
  allBudgets = [],
  filterCliente, setFilterCliente,
  filterObra, setFilterObra,
  filterPresupuesto, setFilterPresupuesto,
  testIdPrefix = "filtro",
}) {
  const obrasFilterList = filterCliente
    ? listasObras.filter(o => o.cliente_id === filterCliente)
    : listasObras;
  const obraFilterSel = listasObras.find(o => o._id === filterObra);
  const presupuestosFilterList = filtrarPresupuestos(allBudgets, { clienteId: filterCliente, obraSel: obraFilterSel, listasClientes, listasObras });

  // Fix DY (2026-09-23): los tres con buscador; mismos valores y mismas cascadas.
  return (
    <>
      <SelectBuscable
        value={filterCliente}
        onChange={v => { setFilterCliente(v); setFilterObra(""); setFilterPresupuesto(""); }}
        vacio="Todos los clientes"
        options={listasClientes.map(c => ({ value: c._id, label: `${c.codigo} · ${c.cliente}` }))}
        className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white max-w-[150px]"
        testId={`${testIdPrefix}-cliente-filter`}
      />
      <SelectBuscable
        value={filterObra}
        onChange={v => { setFilterObra(v); setFilterPresupuesto(""); }}
        vacio="Todas las obras"
        options={obrasFilterList.map(o => ({ value: o._id, label: `${o.codigo} · ${o.obra}` }))}
        className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white max-w-[150px]"
        testId={`${testIdPrefix}-obra-filter`}
      />
      <SelectBuscable
        value={filterPresupuesto}
        onChange={v => setFilterPresupuesto(v)}
        vacio="Todos los presupuestos"
        options={[
          { value: OBRA_RAPIDA_FILTER, label: "Pedido para obra rápida / A vincular con presupuesto" },
          ...presupuestosFilterList.map(b => ({ value: b._id || b.id, label: `${b.number} · ${b.nombre || b.obra}` })),
        ]}
        className="border border-gray-200 rounded-xl px-3 py-2 text-xs bg-white max-w-[190px]"
        testId={`${testIdPrefix}-presupuesto-filter`}
      />
    </>
  );
}

// Criterio de filtrado compartido — usa lista_cliente_id/lista_obra_id/presupuesto_id/
// obra_rapida del documento (PM/PP/AP/PF/OC/Retiro, todos heredan estos campos en
// cascada desde el PM de origen, Bloque 14/16/27).
//
// BLOQUE 33 PARTE A — "defensa extra": si un documento histórico todavía no tiene
// el ID (ej. el backfill de identificación de Bloque 27 no llegó a correr en
// producción) pero SÍ tiene el nombre (lista_cliente_nombre/lista_obra_nombre),
// se lo matchea por nombre contra el cliente/obra seleccionado en el filtro —
// tolera históricos parcialmente completos sin exigir volver a correr ninguna
// migración. `catalogos` es opcional (retro-compat: sin él, comportamiento
// idéntico al de antes, solo match exacto por ID).
const _norm = (s) => (s || "").toString().trim().toLowerCase();

export function matchClienteObraPresupuesto(doc, { filterCliente, filterObra, filterPresupuesto }, catalogos = {}) {
  const { listasClientes = [], listasObras = [] } = catalogos;

  let matchCliente = !filterCliente || doc.lista_cliente_id === filterCliente;
  if (!matchCliente && filterCliente && !doc.lista_cliente_id && doc.lista_cliente_nombre) {
    const cli = listasClientes.find(c => c._id === filterCliente);
    const nombreDoc = _norm(doc.lista_cliente_nombre);
    if (cli) {
      const nombreCli = _norm(cli.cliente);
      matchCliente = _mismoCliente(nombreDoc, nombreCli, listasClientes);
    }
  }

  let matchObra = !filterObra || doc.lista_obra_id === filterObra;
  if (!matchObra && filterObra && !doc.lista_obra_id && doc.lista_obra_nombre) {
    const obr = listasObras.find(o => o._id === filterObra);
    const nombreDoc = _norm(doc.lista_obra_nombre);
    if (obr) {
      const nombreObr = _norm(obr.obra);
      matchObra = !!nombreDoc && !!nombreObr && (nombreDoc === nombreObr || nombreDoc.includes(nombreObr) || nombreObr.includes(nombreDoc));
    }
  }

  let matchPresupuesto = true;
  if (filterPresupuesto === OBRA_RAPIDA_FILTER) matchPresupuesto = !!doc.obra_rapida;
  else if (filterPresupuesto) matchPresupuesto = doc.presupuesto_id === filterPresupuesto;
  return matchCliente && matchObra && matchPresupuesto;
}

export function hasClienteObraPresupuestoFilter({ filterCliente, filterObra, filterPresupuesto }) {
  return !!(filterCliente || filterObra || filterPresupuesto);
}
